import React, { useState, useEffect, useRef, useCallback, useLayoutEffect } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  Plus, 
  Search, 
  Edit, 
  Trash2, 
  Eye, 
  AlertTriangle,
  FileText,
  Grid,
  List,
  Settings,
  Filter
} from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { useMultipleCodelists } from '../hooks/useCodelist';
import { useDocumentsPreferences } from '../hooks/usePreferences';
import { useAuthStore } from '../store/authStore';
import { api } from '../lib/api';
import { MarkdownEditor } from '../components/documents/MarkdownEditor';
import { DocumentViewer } from '../components/documents/DocumentViewer';
import DocumentsTable from '../components/documents/DocumentsTable';
import DocumentColumnConfig from '../components/documents/DocumentColumnConfig';

interface Document {
  id: number;
  title: string;
  description?: string;
  category?: string;
  nis2_chapter?: string;
  nis2_article?: string;
  nis2_letter?: string;
  status: 'draft' | 'review' | 'approved' | 'archived';
  version: number;
  created_by: number;
  creator_first_name: string;
  creator_last_name: string;
  created_at: string;
  updated_at: string;
  tags?: string[];
  review_date?: string;
  review_period?: string;
  content?: string;
}

type ViewMode = 'list' | 'create' | 'edit' | 'view';

export function DocumentsPage() {
  const [documents, setDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [selectedDocument, setSelectedDocument] = useState<Document | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [selectedStatus, setSelectedStatus] = useState<string>('');
  const [selectedNis2Article, setSelectedNis2Article] = useState<string>('');
  const [selectedNis2Letter, setSelectedNis2Letter] = useState<string>('');
  const [showFilters, setShowFilters] = useState(false);
  const [categories, setCategories] = useState<any[]>([]);
  
  // Bulk selection state
  const [selectedDocumentIds, setSelectedDocumentIds] = useState<Set<number>>(new Set());
  const [selectAllMode, setSelectAllMode] = useState<'none' | 'page' | 'all'>('none');
  const [totalFilteredDocuments, setTotalFilteredDocuments] = useState(0);
  
  // User preferences for view mode and table configuration
  const { 
    viewMode: prefViewMode, 
    visibleColumns, 
    setViewMode: setPrefViewMode, 
    setVisibleColumns, 
    isReady: preferencesReady 
  } = useDocumentsPreferences();
  const [showColumnConfig, setShowColumnConfig] = useState(false);
  
  // Sorting and filtering
  const [sortConfig, setSortConfig] = useState<{ key: string | null; direction: 'asc' | 'desc' }>({
    key: null,
    direction: 'asc'
  });
  const [columnFilters, setColumnFilters] = useState<Record<string, string>>({});
  const columnFiltersRef = useRef<Record<string, string>>({});
  const [shouldRestoreColumnFilter, setShouldRestoreColumnFilter] = useState<{
    restore: boolean;
    column: string;
    position: number;
  }>({ restore: false, column: '', position: 0 });

  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0,
    hasNext: false,
    hasPrev: false
  });
  const paginationRef = useRef(pagination);

  // Keep refs in sync with state
  useEffect(() => {
    columnFiltersRef.current = columnFilters;
  }, [columnFilters]);

  useEffect(() => {
    paginationRef.current = pagination;
  }, [pagination]);
  
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [shouldRestoreFocus, setShouldRestoreFocus] = useState<{
    restore: boolean;
    position: number;
  }>({ restore: false, position: 0 });
  
  const { toast } = useToast();
  const { user } = useAuthStore();
  
  // Load codelists for display names
  const { getItemName } = useMultipleCodelists(['nis2_chapters', 'nis2_articles', 'nis2_letters', 'document_categories']);

  const loadDocuments = useCallback(async (currentPage?: number) => {
    // Don't load documents until preferences are ready
    if (!preferencesReady) {
      return;
    }
    
    try {
      // Save current cursor position and focus state for search input
      const activeElement = document.activeElement;
      const isSearchFocused = activeElement === searchInputRef.current;
      const searchCursorPosition = isSearchFocused ? (searchInputRef.current?.selectionStart || 0) : 0;
      
      // Check if a column filter input is focused
      let columnFilterFocus = null;
      if (activeElement && activeElement.tagName === 'INPUT') {
        const filterInput = activeElement as HTMLInputElement;
        const columnName = filterInput.getAttribute('data-column');
        if (columnName && columnFiltersRef.current.hasOwnProperty(columnName)) {
          columnFilterFocus = {
            column: columnName,
            position: filterInput.selectionStart || 0
          };
        }
      }
      
      setLoading(true);
      
      // Use provided page or current pagination page
      const pageToLoad = currentPage !== undefined ? currentPage : paginationRef.current.page;
      
      // Build query parameters
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      if (selectedCategory) params.append('category', selectedCategory);
      if (selectedStatus) params.append('status', selectedStatus);
      if (selectedNis2Article) params.append('nis2_article', selectedNis2Article);
      if (selectedNis2Letter) params.append('nis2_letter', selectedNis2Letter);
      
      // Add column filters
      const activeColumnFilters = Object.fromEntries(
        Object.entries(columnFiltersRef.current).filter(([_, value]) => value.trim())
      );
      if (Object.keys(activeColumnFilters).length > 0) {
        params.append('column_filters', JSON.stringify(activeColumnFilters));
      }
      
      // Add sorting
      if (sortConfig.key) {
        params.append('sortBy', sortConfig.key);
        params.append('sortOrder', sortConfig.direction);
      } else {
        params.append('sortBy', 'updated_at');
        params.append('sortOrder', 'desc');
      }
      
      params.append('page', pageToLoad.toString());
      params.append('limit', paginationRef.current.limit.toString());

      const response = await api.get<{ 
        success: boolean; 
        data: Document[]; 
        pagination: {
          page: number;
          limit: number;
          total: number;
          pages: number;
          hasNext: boolean;
          hasPrev: boolean;
        }
      }>(`/documents?${params.toString()}`);
      
      if (response.success) {
        // Set focus restoration state before updating React state
        if (isSearchFocused) {
          setShouldRestoreFocus({ restore: true, position: searchCursorPosition });
        }
        
        if (columnFilterFocus) {
          setShouldRestoreColumnFilter({ 
            restore: true, 
            column: columnFilterFocus.column,
            position: columnFilterFocus.position 
          });
        }
        
        setDocuments(response.data);
        setPagination(response.pagination);
        setTotalFilteredDocuments(response.pagination.total);
      }
    } catch (error) {
      console.error('Failed to load documents:', error);
      toast({
        title: "Chyba",
        description: "Nepodařilo se načíst dokumenty",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [searchTerm, selectedCategory, selectedStatus, selectedNis2Article, selectedNis2Letter, sortConfig, toast, preferencesReady]);

  // Load initial data when preferences are ready
  useEffect(() => {
    if (preferencesReady) {
      loadDocuments();
      fetchCategories();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preferencesReady]);

  // Restore focus after React re-render
  useLayoutEffect(() => {
    if (shouldRestoreFocus.restore && searchInputRef.current) {
      const restoreFocus = () => {
        if (searchInputRef.current) {
          searchInputRef.current.focus();
          searchInputRef.current.setSelectionRange(shouldRestoreFocus.position, shouldRestoreFocus.position);
        }
      };
      
      restoreFocus();
      setTimeout(restoreFocus, 0);
      setTimeout(restoreFocus, 10);
      requestAnimationFrame(restoreFocus);
      
      setShouldRestoreFocus({ restore: false, position: 0 });
    }
  }, [shouldRestoreFocus, documents]);

  // Restore focus for column filter inputs after React re-render
  useLayoutEffect(() => {
    if (shouldRestoreColumnFilter.restore && shouldRestoreColumnFilter.column) {
      const restoreColumnFilterFocus = () => {
        const filterInput = document.querySelector(`input[data-column="${shouldRestoreColumnFilter.column}"]`) as HTMLInputElement;
        if (filterInput) {
          filterInput.focus();
          filterInput.setSelectionRange(shouldRestoreColumnFilter.position, shouldRestoreColumnFilter.position);
          filterInput.dispatchEvent(new FocusEvent('focus', { bubbles: true }));
        }
      };
      
      restoreColumnFilterFocus();
      setTimeout(restoreColumnFilterFocus, 0);
      setTimeout(restoreColumnFilterFocus, 10);
      requestAnimationFrame(restoreColumnFilterFocus);
      
      setShouldRestoreColumnFilter({ restore: false, column: '', position: 0 });
    }
  }, [shouldRestoreColumnFilter, documents]);

  // Load documents when filters change (immediate) - reset to page 1
  useEffect(() => {
    if (preferencesReady) {
      loadDocuments(1);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategory, selectedStatus, selectedNis2Article, selectedNis2Letter, preferencesReady]);

  // Load documents when sorting changes (immediate) - keep current page
  useEffect(() => {
    if (preferencesReady) {
      loadDocuments();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortConfig, preferencesReady]);

  // Separate effect for debounced column filters
  useEffect(() => {
    const hasActiveFilters = Object.values(columnFilters).some(value => value.trim());
    
    if (!hasActiveFilters) {
      setTimeout(() => {
        const activeElement = document.activeElement;
        const isColumnFilterFocused = activeElement && 
          activeElement.tagName === 'INPUT' && 
          activeElement.getAttribute('data-column');
        
        if (isColumnFilterFocused) {
          return;
        }
        
        const timeout = setTimeout(() => {
          if (pagination.page !== 1) {
            setPagination(prev => ({ ...prev, page: 1 }));
            loadDocuments(1);
          } else {
            loadDocuments();
          }
        }, 500);
        
        return () => clearTimeout(timeout);
      }, 10);
      
      return;
    }
    
    const timeout = setTimeout(() => {
      if (pagination.page !== 1) {
        setPagination(prev => ({ ...prev, page: 1 }));
        loadDocuments(1);
      } else {
        loadDocuments();
      }
    }, 500);

    return () => clearTimeout(timeout);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columnFilters, pagination.page]);

  // Separate effect for debounced search
  useEffect(() => {
    if (searchTerm === '') {
      if (pagination.page !== 1) {
        setPagination(prev => ({ ...prev, page: 1 }));
        loadDocuments(1);
      } else {
        loadDocuments();
      }
      return;
    }

    const timeout = setTimeout(() => {
      loadDocuments(1);
    }, 500);

    return () => clearTimeout(timeout);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  const fetchCategories = async () => {
    try {
      const response = await api.get('/documents/categories') as any;
      setCategories(response.categories);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  const handleDeleteDocument = async (documentId: number) => {
    if (!window.confirm('Opravdu chcete smazat tento dokument? Tato akce je nevratná a smaže i všechny verze dokumentu.')) {
      return;
    }

    try {
      await api.delete(`/documents/${documentId}`);
      toast({
        title: "Úspěch",
        description: "Dokument byl smazán",
      });
      // Remove from selected documents if it was selected
      setSelectedDocumentIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(documentId);
        return newSet;
      });
      loadDocuments();
    } catch (error: any) {
      console.error('Failed to delete document:', error);
      toast({
        title: "Chyba",
        description: error.response?.data?.error || "Nepodařilo se smazat dokument",
        variant: "destructive",
      });
    }
  };

  // Bulk selection functions
  const handleSelectDocument = (documentId: number, selected: boolean) => {
    setSelectedDocumentIds(prev => {
      const newSet = new Set(prev);
      if (selected) {
        newSet.add(documentId);
      } else {
        newSet.delete(documentId);
      }
      return newSet;
    });
    
    // Reset select all mode when individual selection changes
    if (selectAllMode !== 'none') {
      setSelectAllMode('none');
    }
  };

  const handleSelectAll = async (mode: 'none' | 'page' | 'all') => {
    setSelectAllMode(mode);
    
    if (mode === 'none') {
      setSelectedDocumentIds(new Set());
    } else if (mode === 'page') {
      const currentPageDocumentIds = documents.filter(doc => doc.id).map(doc => doc.id!);
      setSelectedDocumentIds(new Set(currentPageDocumentIds));
    } else if (mode === 'all') {
      // Fetch all filtered document IDs from backend
      try {
        const params = new URLSearchParams();
        if (searchTerm) params.append('search', searchTerm);
        if (selectedCategory) params.append('category', selectedCategory);
        if (selectedStatus) params.append('status', selectedStatus);
        if (selectedNis2Article) params.append('nis2_article', selectedNis2Article);
        if (selectedNis2Letter) params.append('nis2_letter', selectedNis2Letter);
        
        const activeColumnFilters = Object.fromEntries(
          Object.entries(columnFilters).filter(([_, value]) => value.trim())
        );
        if (Object.keys(activeColumnFilters).length > 0) {
          params.append('column_filters', JSON.stringify(activeColumnFilters));
        }
        
        if (sortConfig.key) {
          params.append('sortBy', sortConfig.key);
          params.append('sortOrder', sortConfig.direction);
        } else {
          params.append('sortBy', 'updated_at');
          params.append('sortOrder', 'desc');
        }
        
        params.append('page', '1');
        params.append('limit', totalFilteredDocuments.toString());
        
        const response = await api.get<{ 
          success: boolean; 
          data: Document[];
        }>(`/documents?${params.toString()}`);
        
        if (response.success) {
          const allFilteredDocumentIds = response.data.filter(doc => doc.id).map(doc => doc.id!);
          setSelectedDocumentIds(new Set(allFilteredDocumentIds));
        } else {
          console.error('Failed to fetch all document IDs for selection');
          toast({
            title: "Chyba",
            description: "Nepodařilo se načíst všechny dokumenty pro výběr",
            variant: "destructive",
          });
          setSelectAllMode('none');
        }
      } catch (error) {
        console.error('Error fetching all documents for selection:', error);
        toast({
          title: "Chyba",
          description: "Nepodařilo se načíst všechny dokumenty pro výběr",
          variant: "destructive",
        });
        setSelectAllMode('none');
      }
    }
  };

  const clearSelection = () => {
    setSelectedDocumentIds(new Set());
    setSelectAllMode('none');
  };

  const getSelectedCount = () => {
    if (selectAllMode === 'all') {
      return totalFilteredDocuments;
    }
    return selectedDocumentIds.size;
  };

  const handleBulkDelete = async () => {
    const selectedCount = getSelectedCount();
    
    if (selectedCount === 0) {
      toast({
        title: "Chyba",
        description: "Nejsou vybrány žádné dokumenty pro smazání",
        variant: "destructive",
      });
      return;
    }

    if (!window.confirm(`Opravdu chcete smazat ${selectedCount} vybraných dokumentů? Tato akce je nevratná.`)) {
      return;
    }

    try {
      const documentIdsToDelete = Array.from(selectedDocumentIds);

      const response = await api.post<{ 
        success: boolean; 
        message: string;
        data: {
          successful_deletions: Array<{ document_id: number; deleted: boolean }>;
          failed_deletions: Array<{ document_id: number; error: string }>;
        }
      }>('/documents/bulk-delete', {
        document_ids: documentIdsToDelete
      });

      if (response.success) {
        const { successful_deletions, failed_deletions } = response.data;
        
        toast({
          title: "Úspěch",
          description: `${successful_deletions.length} dokumentů bylo úspěšně smazáno${failed_deletions.length > 0 ? `, ${failed_deletions.length} se nepodařilo smazat` : ''}`,
        });

        if (failed_deletions.length > 0) {
          console.warn('Failed deletions:', failed_deletions);
        }

        clearSelection();
        loadDocuments();
      }
    } catch (error) {
      console.error('Failed to bulk delete documents:', error);
      toast({
        title: "Chyba",
        description: "Nepodařilo se smazat vybrané dokumenty",
        variant: "destructive",
      });
    }
  };

  const handleCreateDocument = async (documentData: any) => {
    try {
      await api.post('/documents', documentData);
      toast({
        title: 'Úspěch',
        description: 'Dokument byl vytvořen'
      });
      setViewMode('list');
      loadDocuments();
    } catch (error: any) {
      console.error('Failed to create document:', error);
      toast({
        title: 'Chyba',
        description: error.response?.data?.error || 'Nepodařilo se vytvořit dokument',
        variant: 'destructive'
      });
    }
  };

  const handleEditDocument = async (documentData: any) => {
    if (!selectedDocument) return;
    
    try {
      await api.put(`/documents/${selectedDocument.id}`, documentData);
      toast({
        title: 'Úspěch',
        description: 'Dokument byl upraven'
      });
      setViewMode('view');
      loadDocuments();
      // Refresh selected document
      const response = await api.get(`/documents/${selectedDocument.id}?includeContent=true`) as any;
      setSelectedDocument(response);
    } catch (error: any) {
      console.error('Failed to update document:', error);
      toast({
        title: 'Chyba',
        description: error.response?.data?.error || 'Nepodařilo se upravit dokument',
        variant: 'destructive'
      });
    }
  };

  const handleViewDocument = (document: Document) => {
    setSelectedDocument(document);
    setViewMode('view');
  };

  const handleEditDocumentClick = async (document: Document) => {
    try {
      const response = await api.get(`/documents/${document.id}?includeContent=true`) as any;
      setSelectedDocument(response);
      setViewMode('edit');
    } catch (error) {
      console.error('Failed to load document content:', error);
      toast({
        title: 'Chyba',
        description: 'Nepodařilo se načíst obsah dokumentu',
        variant: 'destructive'
      });
    }
  };

  const canEditDocument = (document: Document) => {
    return user?.role === 'admin' || document.created_by === user?.id;
  };

  const canDeleteDocument = (document: Document) => {
    return user?.role === 'admin' || document.created_by === user?.id;
  };

  const canApproveDocument = () => {
    return ['admin', 'auditor'].includes(user?.role || '');
  };

  const handlePageChange = (newPage: number) => {
    loadDocuments(newPage);
  };

  const clearAllFilters = () => {
    setSearchTerm('');
    setSelectedCategory('');
    setSelectedStatus('');
    setSelectedNis2Article('');
    setSelectedNis2Letter('');
    setColumnFilters({});
    clearSelection();
  };

  // Sorting functionality - cycles through: none -> asc -> desc -> none
  const handleSort = (key: string) => {
    setSortConfig(prevConfig => {
      if (prevConfig.key === key) {
        if (prevConfig.direction === 'asc') {
          return { key, direction: 'desc' };
        } else if (prevConfig.direction === 'desc') {
          return { key: null, direction: 'asc' };
        }
      }
      return { key, direction: 'asc' };
    });
  };

  // Column filtering functionality
  const handleColumnFilter = (column: string, value: string) => {
    setColumnFilters(prev => {
      const newFilters = { ...prev };
      
      if (value.trim()) {
        newFilters[column] = value;
      } else {
        delete newFilters[column];
      }
      
      return newFilters;
    });
  };

  const handleClearColumnFilter = (column: string) => {
    setColumnFilters(prev => {
      const newFilters = { ...prev };
      delete newFilters[column];
      return newFilters;
    });
    
    setTimeout(() => {
      loadDocuments(1);
    }, 0);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'draft': return 'bg-gray-100 text-gray-800';
      case 'review': return 'bg-yellow-100 text-yellow-800';
      case 'approved': return 'bg-green-100 text-green-800';
      case 'archived': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'draft': return 'Návrh';
      case 'review': return 'Na schválení';
      case 'approved': return 'Schváleno';
      case 'archived': return 'Archivováno';
      default: return status;
    }
  };

  const getCategoryLabel = (category: string) => {
    const cat = categories.find(c => c.value === category);
    return cat ? cat.label : category;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('cs-CZ');
  };


  // Render different views based on mode
  if (viewMode === 'create') {
    return (
      <MarkdownEditor
        onSave={handleCreateDocument}
        onCancel={() => setViewMode('list')}
        categories={categories}
        nis2Chapters={[]}
      />
    );
  }

  if (viewMode === 'edit' && selectedDocument) {
    return (
      <MarkdownEditor
        title={selectedDocument.title}
        content={selectedDocument.content || ''}
        description={selectedDocument.description}
        category={selectedDocument.category}
        nis2_chapter={selectedDocument.nis2_chapter}
        nis2_article={selectedDocument.nis2_article}
        nis2_letter={selectedDocument.nis2_letter}
        tags={selectedDocument.tags}
        review_date={selectedDocument.review_date}
        review_period={selectedDocument.review_period}
        onSave={handleEditDocument}
        onCancel={() => setViewMode('view')}
        categories={categories}
        nis2Chapters={[]}
      />
    );
  }

  if (viewMode === 'view' && selectedDocument) {
    return (
      <DocumentViewer
        documentId={selectedDocument.id}
        onEdit={async () => {
          try {
            const response = await api.get(`/documents/${selectedDocument.id}?includeContent=true`) as any;
            setSelectedDocument(response);
            setViewMode('edit');
          } catch (error) {
            console.error('Failed to load document content:', error);
            toast({
              title: 'Chyba',
              description: 'Nepodařilo se načíst obsah dokumentu',
              variant: 'destructive'
            });
          }
        }}
        onBack={() => setViewMode('list')}
        canEdit={canEditDocument(selectedDocument)}
        canApprove={canApproveDocument()}
      />
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Dokumenty</h1>
          <p className="text-muted-foreground">
            Správa ISMS dokumentů s verzováním a mapováním na NIS2
          </p>
        </div>
        <div className="flex gap-2">
          {/* View mode toggle */}
          <div className="flex border rounded-lg">
            <Button
              variant={prefViewMode === 'cards' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setPrefViewMode('cards')}
              className="rounded-r-none"
            >
              <Grid className="h-4 w-4" />
            </Button>
            <Button
              variant={prefViewMode === 'table' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setPrefViewMode('table')}
              className="rounded-l-none"
            >
              <List className="h-4 w-4" />
            </Button>
          </div>
          
          {/* Column config button (only visible in table mode) */}
          {prefViewMode === 'table' && (
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => setShowColumnConfig(true)}
            >
              <Settings className="h-4 w-4 mr-2" />
              Sloupce
            </Button>
          )}
          
          <Button onClick={() => setViewMode('create')}>
            <Plus className="h-4 w-4 mr-2" />
            Nový dokument
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  ref={searchInputRef}
                  placeholder="Vyhledat dokumenty..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9"
                />
              </div>
            </div>
            <div className="flex gap-2">
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="px-3 py-2 border border-input bg-background rounded-md text-sm"
              >
                <option value="">Všechny kategorie</option>
                {categories.map(category => (
                  <option key={category.value} value={category.value}>
                    {category.label}
                  </option>
                ))}
              </select>
              <select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                className="px-3 py-2 border border-input bg-background rounded-md text-sm"
              >
                <option value="">Všechny stavy</option>
                <option value="draft">Návrh</option>
                <option value="review">Na schválení</option>
                <option value="approved">Schváleno</option>
                <option value="archived">Archivováno</option>
              </select>
              <Button
                variant="outline"
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center gap-2"
              >
                <Filter className="h-4 w-4" />
                Filtry
              </Button>
            </div>
          </div>
          
          {showFilters && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4 pt-4 border-t">
              <div>
                <label className="block text-sm font-medium mb-2">NIS2 Article</label>
                <select
                  value={selectedNis2Article}
                  onChange={(e) => setSelectedNis2Article(e.target.value)}
                  className="w-full px-3 py-2 border border-input bg-background rounded-md text-sm"
                >
                  <option value="">Všechny články</option>
                  <option value="article-20">Article 20</option>
                  <option value="article-21">Article 21</option>
                  <option value="article-22">Article 22</option>
                  <option value="article-23">Article 23</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">NIS2 Letter</label>
                <select
                  value={selectedNis2Letter}
                  onChange={(e) => setSelectedNis2Letter(e.target.value)}
                  className="w-full px-3 py-2 border border-input bg-background rounded-md text-sm"
                >
                  <option value="">Všechna písmena</option>
                  <option value="a">a)</option>
                  <option value="b">b)</option>
                  <option value="c">c)</option>
                  <option value="d">d)</option>
                  <option value="e">e)</option>
                  <option value="f">f)</option>
                  <option value="g">g)</option>
                  <option value="h">h)</option>
                  <option value="i">i)</option>
                  <option value="j">j)</option>
                </select>
              </div>

              <div className="flex items-end">
                <Button variant="outline" onClick={clearAllFilters} className="w-full">
                  Vymazat filtry
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Bulk Actions Bar */}
      {getSelectedCount() > 0 && (
        <Card className="mb-4">
          <CardContent className="py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <span className="text-sm font-medium">
                  {selectAllMode === 'all' 
                    ? `Vybráno všech ${totalFilteredDocuments} dokumentů`
                    : `Vybráno ${getSelectedCount()} dokumentů`
                  }
                </span>
                {selectAllMode === 'all' && (
                  <span className="text-xs text-muted-foreground">
                    (všechny vyfiltrované dokumenty)
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={handleBulkDelete}
                  className="flex items-center gap-2"
                >
                  <Trash2 className="h-4 w-4" />
                  Smazat vybrané
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={clearSelection}
                >
                  Zrušit výběr
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Documents Display - Cards or Table */}
      {prefViewMode === 'cards' ? (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {documents.map((document) => (
            <Card key={document.id} className="hover:shadow-md transition-shadow">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <FileText className="h-4 w-4" />
                    <CardTitle className="text-lg">{document.title}</CardTitle>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className={getStatusColor(document.status)}>
                      {getStatusLabel(document.status)}
                    </Badge>
                    <span className="text-sm text-gray-500">v{document.version}</span>
                  </div>
                </div>
                {document.description && (
                  <CardDescription className="line-clamp-2">
                    {document.description}
                  </CardDescription>
                )}
              </CardHeader>
              <CardContent className="pt-0">
                <div className="space-y-2">
                  {document.category && (
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Kategorie:</span>
                      <Badge variant="secondary">{getCategoryLabel(document.category)}</Badge>
                    </div>
                  )}
                  
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Autor:</span>
                    <span className="text-right">{document.creator_first_name} {document.creator_last_name}</span>
                  </div>
                  
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Upraveno:</span>
                    <span className="text-right">{formatDate(document.updated_at)}</span>
                  </div>

                  {document.nis2_article && document.nis2_letter && (
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">NIS2:</span>
                      <span className="text-right">{getItemName('nis2_articles', document.nis2_article)} - {getItemName('nis2_letters', document.nis2_letter)}</span>
                    </div>
                  )}

                  {document.review_date && (
                    <div className="flex items-center space-x-1 text-amber-600 text-sm">
                      <AlertTriangle className="h-3 w-3" />
                      <span>Kontrola: {formatDate(document.review_date)}</span>
                    </div>
                  )}
                </div>

                {document.tags && document.tags.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-3">
                    {document.tags.map((tag, index) => (
                      <Badge key={index} variant="secondary" className="text-xs">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                )}

                <div className="flex justify-end space-x-1 mt-4">
                  <Button variant="outline" size="sm" onClick={() => handleViewDocument(document)}>
                    <Eye className="h-3 w-3" />
                  </Button>
                  {canEditDocument(document) && (
                    <Button variant="outline" size="sm" onClick={() => handleEditDocumentClick(document)}>
                      <Edit className="h-3 w-3" />
                    </Button>
                  )}
                  {canDeleteDocument(document) && (
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => handleDeleteDocument(document.id)}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="p-0">
            <DocumentsTable
              documents={documents}
              onViewDocument={handleViewDocument}
              onEditDocument={handleEditDocumentClick}
              onDeleteDocument={handleDeleteDocument}
              getItemName={getItemName}
              visibleColumns={visibleColumns}
              sortConfig={sortConfig}
              onSort={handleSort}
              columnFilters={columnFilters}
              onColumnFilter={handleColumnFilter}
              onClearColumnFilter={handleClearColumnFilter}
              selectedDocumentIds={selectedDocumentIds}
              selectAllMode={selectAllMode}
              totalFilteredDocuments={totalFilteredDocuments}
              onSelectDocument={handleSelectDocument}
              onSelectAll={handleSelectAll}
              canEditDocument={canEditDocument}
              canDeleteDocument={canDeleteDocument}
            />
          </CardContent>
        </Card>
      )}

      {/* Show filter summary */}
      {(searchTerm || selectedCategory || selectedStatus || selectedNis2Article || selectedNis2Letter || Object.values(columnFilters).some(f => f.trim())) && (
        <Card className="mb-4">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <span>Aktivní filtry:</span>
              {searchTerm && <Badge variant="secondary">Hledání: "{searchTerm}"</Badge>}
              {selectedCategory && <Badge variant="secondary">Kategorie: {getCategoryLabel(selectedCategory)}</Badge>}
              {selectedStatus && <Badge variant="secondary">Status: {getStatusLabel(selectedStatus)}</Badge>}
              {selectedNis2Article && <Badge variant="secondary">NIS2 Article: {selectedNis2Article}</Badge>}
              {selectedNis2Letter && <Badge variant="secondary">NIS2 Letter: {selectedNis2Letter}</Badge>}
              {Object.entries(columnFilters).map(([column, value]) => 
                value.trim() && (
                  <Badge key={column} variant="secondary">
                    {column}: "{value}"
                  </Badge>
                )
              )}
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={clearAllFilters}
                className="ml-2"
              >
                Vymazat filtry
              </Button>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              Zobrazeno {documents.length} z {pagination.total} dokumentů 
              {prefViewMode === 'table' && sortConfig.key && (
                <span> • Řazeno podle: {sortConfig.key} ({sortConfig.direction === 'asc' ? 'vzestupně' : 'sestupně'})</span>
              )}
            </p>
          </CardContent>
        </Card>
      )}

      {documents.length === 0 && !loading && (
        <Card>
          <CardContent className="pt-6">
            <div className="text-center py-8">
              <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium mb-2">Žádné dokumenty nenalezeny</h3>
              <p className="text-muted-foreground mb-4">
                {searchTerm || selectedCategory || selectedStatus
                  ? `Žádné dokumenty neodpovídají současným filtrům. Zkuste vymazat filtry pro zobrazení všech ${pagination.total} dokumentů.`
                  : "Začněte vytvořením prvního dokumentu."}
              </p>
              {!searchTerm && !selectedCategory && !selectedStatus ? (
                <Button onClick={() => setViewMode('create')}>
                  <Plus className="h-4 w-4 mr-2" />
                  Vytvořit první dokument
                </Button>
              ) : (
                <Button 
                  variant="outline" 
                  onClick={clearAllFilters}
                >
                  Vymazat všechny filtry
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Pagination */}
      {pagination.pages > 1 && (
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-600">
                Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} documents
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={!pagination.hasPrev}
                >
                  Previous
                </Button>
                
                <div className="flex items-center gap-1">
                  {Array.from({ length: Math.min(5, pagination.pages) }, (_, i) => {
                    const pageNum = Math.max(1, Math.min(pagination.pages - 4, pagination.page - 2)) + i;
                    if (pageNum > pagination.pages) return null;
                    
                    return (
                      <Button
                        key={pageNum}
                        variant={pageNum === pagination.page ? "default" : "outline"}
                        size="sm"
                        onClick={() => handlePageChange(pageNum)}
                        className="w-8 h-8 p-0"
                      >
                        {pageNum}
                      </Button>
                    );
                  })}
                </div>

                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={!pagination.hasNext}
                >
                  Next
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Column Configuration Modal */}
      <DocumentColumnConfig
        visibleColumns={visibleColumns}
        onVisibleColumnsChange={setVisibleColumns}
        isOpen={showColumnConfig}
        onClose={() => setShowColumnConfig(false)}
      />
    </div>
  );
}